import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { TrendingUp, AlertTriangle, ThumbsDown, Share2, Loader2 } from "lucide-react";
import { Header } from "../components/Header";
import { AdminNav } from "../components/AdminNav";
import { fetchReviewQueue } from "../lib/api";
import { QUEUE } from "../constants/testIds";

const SECTIONS = [
  { kind: "most_searched", title: "Most searched", icon: TrendingUp, color: "text-emerald-500", help: "Top 10 products by number of times recommended. Audit these first." },
  { kind: "lowest_confidence", title: "Lowest confidence", icon: AlertTriangle, color: "text-amber-400", help: "Recommendations the AI was least sure of (confidence < 70%)." },
  { kind: "most_disliked", title: "Most disliked", icon: ThumbsDown, color: "text-red-400", help: "Recommendations with the most user 👎 votes." },
  { kind: "most_shared", title: "Most shared", icon: Share2, color: "text-cyan-400", help: "Best-performing recommendations by share count." },
];

const Row = ({ kind, item, i }) => {
  if (kind === "most_searched") {
    return (
      <div data-testid={QUEUE.item(kind, i)} className="flex items-center justify-between py-2.5 border-b border-border/60 last:border-b-0">
        <div className="min-w-0 pr-3">
          <div className="text-sm truncate text-foreground/90">{item.best_product}</div>
          <div className="text-[11px] text-muted-foreground">
            avg confidence {item.avg_confidence != null ? `${item.avg_confidence}%` : "—"}
          </div>
        </div>
        <div className="text-xs font-semibold text-emerald-500">×{item.count}</div>
      </div>
    );
  }
  return (
    <div data-testid={QUEUE.item(kind, i)} className="flex items-center justify-between py-2.5 border-b border-border/60 last:border-b-0">
      <div className="min-w-0 pr-3">
        <div className="text-sm truncate text-foreground/90">{item.best_product || item.query}</div>
        <div className="text-[11px] text-muted-foreground truncate">{item.query}</div>
      </div>
      <div className="text-xs font-semibold text-emerald-500 whitespace-nowrap">
        {kind === "lowest_confidence" && `${item.confidence}%`}
        {kind === "most_disliked" && `👎 ${item.unhelpful_votes}`}
        {kind === "most_shared" && `⤴ ${item.share_count}`}
      </div>
    </div>
  );
};

export default function AdminReviewQueue() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!localStorage.getItem("sbi_admin_token")) {
      navigate("/admin");
      return;
    }
    fetchReviewQueue()
      .then(setData)
      .catch((e) => {
        if (e?.response?.status === 401) {
          localStorage.removeItem("sbi_admin_token");
          navigate("/admin");
        } else {
          setErr(e?.message || "Failed to load");
        }
      });
  }, [navigate]);

  const logout = () => {
    localStorage.removeItem("sbi_admin_token");
    navigate("/admin");
  };

  return (
    <div className="min-h-screen bg-background" data-testid={QUEUE.page}>
      <Header />
      <main className="max-w-7xl mx-auto px-5 py-8">
        <AdminNav onLogout={logout} />

        <div className="mb-5">
          <h1 className="font-display font-bold text-2xl tracking-tight">Review Queue</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Prioritised lists for hammering 100+ real searches. Audit the high-impact rows first.
          </p>
        </div>

        {err && <div className="text-sm text-red-400">{err}</div>}
        {!data && !err && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground py-10 justify-center">
            <Loader2 className="w-4 h-4 animate-spin text-emerald-500" /> Loading queue…
          </div>
        )}

        {data && (
          <div className="grid md:grid-cols-2 gap-4">
            {SECTIONS.map(({ kind, title, icon: Icon, color, help }) => {
              const items = data[kind] || [];
              return (
                <div key={kind} data-testid={QUEUE.card(kind)} className="bg-card border border-border rounded-2xl p-5">
                  <div className="flex items-center gap-2 mb-1">
                    <Icon className={`w-4 h-4 ${color}`} />
                    <span className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">{title}</span>
                  </div>
                  <p className="text-[11px] text-muted-foreground mb-3 leading-relaxed">{help}</p>
                  {items.length === 0 ? (
                    <div className="text-sm text-muted-foreground py-3">No data yet.</div>
                  ) : (
                    <div>
                      {items.map((item, i) => <Row key={i} kind={kind} item={item} i={i} />)}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}
